import * as React from 'react';
import { Combobox as BaseCombobox } from '@base-ui-components/react/combobox';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { useCloseWhenAnchorHidden, usePopupOpen } from '@/shared/lib/popupAnchor';
import { useFieldContext } from '../Field/FieldContext';
import { Scroller } from '@/shared/ui/Scroller';
import styles from './Combobox.module.css';

/**
 * Селект с поиском, группировкой и мультивыбором — поверх Base UI combobox.
 *
 * Тот же визуальный язык, что у Select: одинаковый триггер, всплывашка, пункты
 * и галочка выбранного. Разница только в том, чего Select не умеет — строка
 * поиска, чипы мультивыбора и свой вариант. Собирать это отдельным контролом с
 * собственной вёрсткой означало бы завести в интерфейсе второй вид выпадающих
 * списков, и человек каждый раз гадал бы, чего от него ждут.
 *
 *     <Combobox
 *       value={cityId}
 *       onValueChange={setCityId}
 *       options={[{ value: 'msk', label: 'Москва', subtitle: 'ЦФО' }]}
 *     />
 *
 * Нужен список без поиска — это Select, а не Combobox с выключенным вводом.
 *
 * @use выбор из списка, когда нужен поиск, группировка или мультивыбор. Список короткий и без поиска — это Select.
 */

export interface ComboboxOption {
  value: string;
  label: string;
  subtitle?: string | null;
  group?: string | null;
  keywords?: string | null;
}

interface CommonProps {
  options: ComboboxOption[];
  placeholder?: string;
  disabled?: boolean;
  emptyText?: string;
  className?: string;
  'aria-label'?: string;
  id?: string;
  'data-in-field'?: string;
  footer?: React.ReactNode;
}

export interface ComboboxSingleProps extends CommonProps {
  multiple?: false;
  value: string | null;
  onValueChange: (value: string | null) => void;
}

export interface ComboboxMultipleProps extends CommonProps {
  multiple: true;
  value: string[];
  onValueChange: (value: string[]) => void;
  display?: 'chips' | 'summary';
}

export type ComboboxProps = ComboboxSingleProps | ComboboxMultipleProps;

export const Combobox: React.FC<ComboboxProps> = (props) => {
  const {
    options,
    placeholder,
    disabled,
    emptyText = 'Ничего не найдено',
    className,
    footer,
    id,
  } = props;

  const field = useFieldContext();
  const filled = props.multiple
    ? props.value.length > 0
    : props.value != null && props.value !== '';

  const inFieldMark = props['data-in-field'] ?? (field ? '' : undefined);

  const { open, onOpenChange, close } = usePopupOpen<[unknown?]>({});
  const positionerRef = useCloseWhenAnchorHidden(close);

  const triggerProps = {
    id: id ?? field?.controlId,
    'aria-label': props['aria-label'],
    'data-in-field': inFieldMark,
    'data-filled': filled ? '' : undefined,
  };

  const byValue = React.useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );

  const filter = React.useCallback(
    (itemValue: unknown, query: string) => {
      const option = byValue.get(String(itemValue));
      if (!option) return false;
      const needle = query.trim().toLowerCase();
      if (needle === '') return true;
      return [option.label, option.subtitle, option.keywords]
        .filter(Boolean)
        .some((text) => String(text).toLowerCase().includes(needle));
    },
    [byValue],
  );

  const values = React.useMemo(() => options.map((option) => option.value), [options]);

  const groups = React.useMemo(() => {
    const order: (string | null)[] = [];
    const byGroup = new Map<string | null, ComboboxOption[]>();
    for (const option of options) {
      const key = option.group ?? null;
      if (!byGroup.has(key)) {
        byGroup.set(key, []);
        order.push(key);
      }
      byGroup.get(key)!.push(option);
    }
    return order.map((key) => ({ label: key, items: byGroup.get(key)! }));
  }, [options]);

  const grouped = groups.length > 1 || groups[0]?.label != null;

  const items = React.useMemo(
    () =>
      grouped
        ? groups.map((group) => ({ value: group.label ?? '', items: group.items.map((o) => o.value) }))
        : values,
    [grouped, groups, values],
  );

  const renderItem = (value: string) => {
    const option = byValue.get(value);
    return option ? renderOption(option) : null;
  };

  const renderOption = (option: ComboboxOption) => (
    <BaseCombobox.Item key={option.value} value={option.value} className={styles.item}>
      <BaseCombobox.ItemIndicator className={styles.indicator}>
        <Check size={13} />
      </BaseCombobox.ItemIndicator>
      <span className={styles.itemText}>
        <span className={styles.itemLabel}>{option.label}</span>
        {option.subtitle && <span className={styles.itemSubtitle}>{option.subtitle}</span>}
      </span>
    </BaseCombobox.Item>
  );

  const list = (
    <BaseCombobox.List className={styles.list}>
      {grouped
        ? (group: { value: string; items: string[] }) => (
            <BaseCombobox.Group key={group.value || '—'} items={group.items}>
              {group.value && (
                <BaseCombobox.GroupLabel className={styles.groupLabel}>
                  {group.value}
                </BaseCombobox.GroupLabel>
              )}
              <BaseCombobox.Collection>
                {(value: string) => renderItem(value)}
              </BaseCombobox.Collection>
            </BaseCombobox.Group>
          )
        : (value: string) => renderItem(value)}
    </BaseCombobox.List>
  );

  const renderPopup = (withSearch: boolean) => (
    <BaseCombobox.Portal>
      <BaseCombobox.Positioner
        sideOffset={6}
        side="bottom"
        align="start"
        className={styles.positioner}
        ref={positionerRef}
      >
        <BaseCombobox.Popup className={styles.popup}>
          {withSearch && (
            <BaseCombobox.Input className={styles.search} placeholder="Поиск" aria-label="Поиск" />
          )}
          <BaseCombobox.Empty className={styles.empty}>{emptyText}</BaseCombobox.Empty>
          <Scroller className={styles.listScroller} viewportClassName={styles.listViewport}>
            {list}
          </Scroller>
          {footer && <div className={styles.footer}>{footer}</div>}
        </BaseCombobox.Popup>
      </BaseCombobox.Positioner>
    </BaseCombobox.Portal>
  );

  if (props.multiple && props.display === 'summary') {
    const selected = props.value;
    const first = selected.length > 0 ? (byValue.get(selected[0])?.label ?? selected[0]) : null;
    return (
      <BaseCombobox.Root
        open={open}
        onOpenChange={onOpenChange}
        items={items}
        multiple
        value={selected}
        onValueChange={(next) => props.onValueChange((next as string[]) ?? [])}
        disabled={disabled}
        filter={filter}
        autoHighlight
      >
        { }
        <BaseCombobox.Trigger className={cn(styles.trigger, className)} {...triggerProps}>
          <span className={styles.value}>
            {first === null ? (
              <span className={styles.placeholder}>{placeholder ?? 'Выберите…'}</span>
            ) : selected.length === 1 ? (
              first
            ) : (
              `${first} и ещё ${selected.length - 1}`
            )}
          </span>
          <ChevronDown size={14} className={styles.icon} />
        </BaseCombobox.Trigger>
        {renderPopup(true)}
      </BaseCombobox.Root>
    );
  }

  if (props.multiple) {
    const selected = props.value;
    return (
      <BaseCombobox.Root
        open={open}
        onOpenChange={onOpenChange}
        items={items}
        multiple
        value={selected}
        onValueChange={(next) => props.onValueChange((next as string[]) ?? [])}
        disabled={disabled}
        filter={filter}
        autoHighlight
      >
        { }
        { }
        <BaseCombobox.Chips
          className={cn(styles.trigger, styles.triggerChips, className)}
          data-filled={filled ? '' : undefined}
          data-in-field={inFieldMark}
        >
          {selected.map((value) => (
            <BaseCombobox.Chip key={value} className={styles.chip}>
              {byValue.get(value)?.label ?? value}
              <BaseCombobox.ChipRemove className={styles.chipRemove} aria-label="Убрать">
                <X size={12} />
              </BaseCombobox.ChipRemove>
            </BaseCombobox.Chip>
          ))}
          { }
          { }
          <BaseCombobox.Trigger className={styles.chipsTrigger} {...triggerProps}>
            {selected.length === 0 && (
              <span className={styles.placeholder}>{placeholder ?? 'Выберите…'}</span>
            )}
            <ChevronDown size={14} className={styles.icon} />
          </BaseCombobox.Trigger>
        </BaseCombobox.Chips>
        { }
        {renderPopup(true)}
      </BaseCombobox.Root>
    );
  }

  return (
    <BaseCombobox.Root
      open={open}
      onOpenChange={onOpenChange}
      items={items}
      value={props.value ?? null}
      onValueChange={(next) => props.onValueChange((next as string | null) ?? null)}
      disabled={disabled}
      filter={filter}
      autoHighlight
    >
      <BaseCombobox.Trigger className={cn(styles.trigger, className)} {...triggerProps}>
        <span className={styles.value}>
          <BaseCombobox.Value>
            {(value: unknown) => {
              const option = byValue.get(String(value ?? ''));
              if (!option) {
                return <span className={styles.placeholder}>{placeholder ?? 'Выберите…'}</span>;
              }
              return option.label;
            }}
          </BaseCombobox.Value>
        </span>
        <ChevronDown size={14} className={styles.icon} />
      </BaseCombobox.Trigger>
      {renderPopup(true)}
    </BaseCombobox.Root>
  );
};
